import Vue from 'vue';
import Vuex from 'vuex';
import createPersistedState from 'vuex-persistedstate';

Vue.use(Vuex);

/** Versão do questionário do /whatsapp — ver `quizWhatsapp`. */
export const VERSAO_QUIZ_WHATSAPP = 1;

function quizWhatsappVazio() {
    return {
        versao: VERSAO_QUIZ_WHATSAPP,
        salvoEm: null,
        enviadoEm: null,
        contatoUuid: null,
        respostas: {},
    };
}

/** Versão do questionário de /revenda/whatsapp — ver `quizRevenda`. */
export const VERSAO_QUIZ_REVENDA = 1;

function quizRevendaVazio() {
    return {
        versao: VERSAO_QUIZ_REVENDA,
        salvoEm: null,
        enviadoEm: null,
        contatoUuid: null,
        respostas: {},
    };
}

export default new Vuex.Store({
    plugins: [
        createPersistedState({
            key: 'zm-lead',
            // `urlParams` NÃO entra aqui: quem persiste atribuição é o
            // [src/utils/attribution.js], que sabe expirar. Com dois donos, este
            // balde (que não expira) manteria vivo um anúncio que o outro já venceu.
            paths: ['selectedPlan', 'quizAnswers', 'contatoUuid', 'quizWhatsapp', 'quizRevenda'],
        }),
    ],

    state: {
        plan: {},
        checkout: false,
        hideWhatsapp: false,
        contatoUuid: null,
        urlParams: {
            ad: null,
            utm: null,
            ref: null,
            pxl: null,
            produto: null,   // 'tablet' quando o lead vem da LP de tablet de mesa
            origem: null,    // utm | utm_source | deduzida do referrer
            utm_params: null,
            fonte: 'direto', // 'url' | 'storage' | 'referrer' | 'direto'
            capturado_em: null,
            visitante_uuid: null,
            primeiro: null,  // primeiro toque: { ad, origem, fonte, capturado_em }
        },
        selectedPlan: null,   // { id, label, price }
        quizAnswers: {},      // respostas acumuladas do quiz do /checkout
        showPlanos: false,    // exibir seção de planos (ativado via ?planos=1)

        // Respostas do quiz do /whatsapp. Balde SEPARADO do `quizAnswers` de
        // propósito: os dois quizzes usam os mesmos `id` de pergunta com valores
        // diferentes (o /checkout grava 'PIZZARIA', o /whatsapp grava 'Pizzaria').
        // Misturados, o lead volta e vê a opção sem acender enquanto o botão
        // "Próxima" fica habilitado — falha silenciosa dos dois lados.
        quizWhatsapp: quizWhatsappVazio(),

        // Respostas do quiz de /revenda/whatsapp. Balde próprio: quem já mandou
        // o quiz de restaurante não pode ver "Você já falou com a gente" no de
        // revenda, e vice-versa — são leads de funis diferentes.
        quizRevenda: quizRevendaVazio(),
    },

    getters: {
        getPrice: state => {
            return state.plan?.price || 99;
        },

        getCheckout: state => {
            return state.plan?.checkout || 'https://mpago.la/2yVBof5';
        },
    },

    mutations: {
        setPlan(state, payload) {
            state.plan = payload;
        },

        setCheckout(state, payload) {
            state.checkout = payload;
        },

        setHideWhatsapp(state, payload) {
            state.hideWhatsapp = !!payload;
        },

        setContatoUuid(state, payload) {
            state.contatoUuid = payload;
        },

        /**
         * Merge parcial. Chave ausente, nula ou vazia é IGNORADA — era daqui que
         * vinha a perda de atribuição: `URLSearchParams.get()` devolve null para
         * chave ausente, e o null entrava no merge apagando o que estava salvo.
         */
        setUrlParams(state, payload) {
            const limpo = {};

            Object.keys(payload || {}).forEach((chave) => {
                const valor = payload[chave];
                if (valor !== null && valor !== undefined && valor !== '') {
                    limpo[chave] = valor;
                }
            });

            state.urlParams = { ...state.urlParams, ...limpo };
        },

        /**
         * Substitui a atribuição inteira pelo que o `attribution.js` resolveu.
         * É substituição e não merge porque lá é que mora a expiração: um `ad`
         * vencido tem que sumir daqui também.
         */
        aplicarAtribuicao(state, payload) {
            state.urlParams = { ...state.urlParams, ...payload };
        },

        setSelectedPlan(state, payload) {
            state.selectedPlan = payload;
        },

        mergeQuizAnswers(state, payload) {
            state.quizAnswers = { ...state.quizAnswers, ...payload };
        },

        setShowPlanos(state, payload) {
            state.showPlanos = !!payload;
        },

        /** Respostas do quiz do /whatsapp, gravadas a cada pergunta respondida. */
        salvarQuizWhatsapp(state, respostas) {
            state.quizWhatsapp = {
                ...state.quizWhatsapp,
                versao: VERSAO_QUIZ_WHATSAPP,
                salvoEm: Date.now(),
                respostas: { ...state.quizWhatsapp.respostas, ...respostas },
            };
        },

        /** Marca que o lead já foi enviado — usado para não duplicar no reenvio. */
        marcarQuizWhatsappEnviado(state, contatoUuid) {
            state.quizWhatsapp = {
                ...state.quizWhatsapp,
                enviadoEm: Date.now(),
                contatoUuid: contatoUuid || null,
            };
        },

        limparQuizWhatsapp(state) {
            state.quizWhatsapp = quizWhatsappVazio();
        },

        /** Mesmas três operações do quiz do /whatsapp, para o quiz de revenda. */
        salvarQuizRevenda(state, respostas) {
            state.quizRevenda = {
                ...state.quizRevenda,
                versao: VERSAO_QUIZ_REVENDA,
                salvoEm: Date.now(),
                respostas: { ...state.quizRevenda.respostas, ...respostas },
            };
        },

        marcarQuizRevendaEnviado(state, contatoUuid) {
            state.quizRevenda = {
                ...state.quizRevenda,
                enviadoEm: Date.now(),
                contatoUuid: contatoUuid || null,
            };
        },

        limparQuizRevenda(state) {
            state.quizRevenda = quizRevendaVazio();
        },

        /**
         * Limpa o lead do funil de checkout. NÃO toca no `quizWhatsapp`: é outro
         * fluxo, e é chamado da confirmação de pagamento e do "recomeçar" do
         * checkout — nenhum dos dois tem a ver com o quiz do /whatsapp.
         */
        clearLead(state) {
            state.selectedPlan = null;
            state.quizAnswers   = {};
            state.contatoUuid   = null;
        },
    },

    actions: {
        //
    },

    modules: {
        //
    },
});
